<template>
    <v-form class="venue-setup" ref="form">
        <div class="step-title">{{ $t('get_started.venue_setup_title') }}</div>
        <div class="step-description">
            {{ $t('get_started.venue_setup_description') }}
        </div>

        <v-text-field
            v-model="form.name"
            :label="$t('general.venue')"
            :placeholder="$t('general.venue_placeholder')"
            :error-messages="form.errors.name"
            :rules="form.rules.name"
            validate-on-blur
            autofocus
        />

        <v-autocomplete
            v-model="curCountry"
            :items="countries"
            :label="$t('general.country')"
            :placeholder="$t('general.country_placeholder')"
            :error-messages="form.errors.country"
            :rules="form.rules.country"
            item-text="name"
            return-object
            clearable
            auto-select-first
            validate-on-blur
            clear-icon="fal fa-times"
        />

        <v-text-field
            v-model="form.address"
            :label="$t('general.venue_address')"
            :placeholder="$t('general.venue_address_placeholder')"
            :error-messages="form.errors.address"
            :rules="form.rules.address"
            validate-on-blur
        />

        <v-row>
            <v-col cols="12" sm="6">
                <v-text-field
                    v-model="form.admin_pin"
                    :type="showPin ? 'text' : 'password'"
                    :label="$t('general.administrator_pin')"
                    :placeholder="$t('general.administrator_pin_placeholder')"
                    :error-messages="form.errors.admin_pin"
                    :rules="form.rules.admin_pin"
                    autocomplete="new-password"
                    :append-icon="showPin ? 'fal fa-eye' : 'fal fa-eye-slash'"
                    @click:append="showPin = !showPin"
                    @input="$refs.repeatPinField.validate()"
                />
            </v-col>

            <v-col cols="12" sm="6">
                <v-text-field
                    ref="repeatPinField"
                    v-model="form.admin_pin_confirmation"
                    :error-messages="form.errors.admin_pin_confirmation"
                    :rules="form.rules.admin_pin_confirmation"
                    :type="showPin ? 'text' : 'password'"
                    :label="$t('general.pin_confirmation')"
                    :placeholder="$t('general.pin_confirmation')"
                />
            </v-col>
        </v-row>

        <v-slide-y-transition>
            <div
                v-if="curCountry && ['MNE', 'SRB', 'BIH'].includes(curCountry.alpha3Code)"
                class="section"
            >
                <div class="accent--text text-right">
                    {{ $t('general.company_info') }}
                </div>

                <v-text-field
                    v-model="form.company_name"
                    :label="$t('general.company_name')"
                    :placeholder="$t('general.company_name_placeholder')"
                    persistent-hint
                    :error-messages="form.errors.company_name"
                    :rules="form.rules.company_name"
                    validate-on-blur
                />

                <v-text-field
                    v-model="form.company_address"
                    class="mb-2"
                    :label="$t('general.company_address')"
                    :placeholder="$t('general.company_address_placeholder')"
                    persistent-hint
                    :error-messages="form.errors.company_address"
                    :rules="form.rules.company_address"
                    validate-on-blur
                />

                <v-row>
                    <v-col class="py-0" cols="12" sm="6">
                        <v-text-field
                            v-model="form.postal_number"
                            :label="$t('general.postal_number')"
                            :placeholder="$t('general.postal_number_placeholder')"
                            :error-messages="form.errors.postal_number"
                            :rules="form.rules.postal_number"
                            validate-on-blur
                        />
                    </v-col>

                    <v-col class="py-0" cols="12" sm="6">
                        <v-text-field
                            v-model="form.city"
                            :label="$t('general.city')"
                            :placeholder="$t('general.city_placeholder')"
                            :error-messages="form.errors.city"
                            :rules="form.rules.city"
                            validate-on-blur
                        />
                    </v-col>
                </v-row>

                <v-row>
                    <v-col class="py-0" cols="12" sm="6">
                        <v-text-field
                            v-model="form.tax_id_number"
                            :label="$t('general.tax_id_number')"
                            :placeholder="$t('general.tax_id_number')"
                            :loading="loadingTaxIdNumber"
                            :disabled="isDisabled"
                            :error-messages="form.errors.tax_id_number"
                            :rules="form.rules.tax_id_number"
                            validate-on-blur
                        />
                    </v-col>
                    <v-col class="py-0" cols="12" sm="6">
                        <v-text-field
                            v-model="form.registration_number"
                            :label="$t('general.registration_number')"
                            :placeholder="$t('general.registration_number_placeholder')"
                            :error-messages="form.errors.registration_number"
                            :rules="form.rules.registration_number"
                            validate-on-blur
                        />
                    </v-col>
                    <v-col class="py-0" cols="12" sm="12">
                        <v-text-field
                            v-model="form.bank_account_number"
                            :label="$t('general.bank_account_number')"
                            :placeholder="$t('general.bank_account_number_placeholder')"
                            :error-messages="form.errors.bank_account_number"
                            :rules="form.rules.bank_account_number"
                            validate-on-blur
                        />
                    </v-col>
                    <v-col
                        v-if="curCountry && curCountry.name === 'Montenegro'"
                        class="py-0"
                        cols="12"
                        sm="6"
                    >
                        <v-text-field
                            v-model="form.operator_code"
                            v-mask="'AA###AA###'"
                            :label="$t('general.operator_code')"
                            :placeholder="$t('general.operator_code')"
                            :hint="$t('general.operator_code_description')"
                            persistent-hint
                            :error-messages="form.errors.operator_code"
                            :rules="form.rules.operator_code"
                            validate-on-blur
                        />
                    </v-col>
                    <v-col
                        v-if="curCountry && curCountry.name === 'Montenegro'"
                        class="py-0"
                        cols="12"
                        sm="6"
                    >
                        <v-text-field
                            v-model="form.hash"
                            v-mask="'AA###AA###'"
                            :label="$t('general.venue_code')"
                            :placeholder="$t('general.venue_code')"
                            :error-messages="form.errors.hash"
                            :rules="form.rules.hash"
                            validate-on-blur
                        />
                    </v-col>
                </v-row>

                <v-row>
                    <v-col
                        v-if="curCountry && curCountry.name === 'Montenegro'"
                        class="py-0"
                        cols="12"
                        sm="6"
                    >
                        <v-file-input
                            :label="$t('general.certificate')"
                            :placeholder="$t('general.certificate')"
                            :error-messages="form.errors.cert ? [$t(`get_started.${form.errors.cert}`)] : []"
                            :rules="form.rules.cert"
                            prepend-icon=""
                            append-icon="fal fa-key"
                            clearable
                            clear-icon="fal fa-times"
                            validate-on-blur
                            @change="form.encodeFile('cert', $event)"
                        />
                    </v-col>

                    <v-col
                        v-if="curCountry && curCountry.name === 'Montenegro'"
                        class="py-0"
                        cols="12"
                        sm="6"
                    >
                        <v-text-field
                            v-model="form.cert_password"
                            :type="showCertPassword ? 'text' : 'password'"
                            :label="$t('general.certificate_password')"
                            :placeholder="$t('general.certificate_password')"
                            :error-messages="form.errors.cert_password"
                            :rules="form.rules.cert_password"
                            autocomplete="new-password"
                            :append-icon="showCertPassword ? 'fal fa-eye' : 'fal fa-eye-slash'"
                            @click:append="showCertPassword = !showCertPassword"
                        />
                    </v-col>
                </v-row>

                <v-row class="mb-3">
                    <v-col
                        class="py-0 d-flex align-center h40 mt-2"
                        cols="12"
                        sm="6"
                    >
                        <v-checkbox
                            v-model="form.taxes_enabled"
                            class="mt-2"
                            :label="$t('get_started.tax_payer')"
                            hide-details
                            validate-on-blur
                        />
                    </v-col>
                    <v-col
                        v-if="curCountry && ['Montenegro', 'Serbia'].includes(curCountry.name) && isAllegra"
                        class="py-0 d-flex align-center h40 mt-2"
                        cols="12"
                        sm="6"
                    >
                        <v-checkbox
                            v-model="form.noncash_only"
                            class="mt-2"
                            :label="$t('get_started.noncash_only')"
                            hide-details
                            validate-on-blur
                        />
                    </v-col>
                    <v-col
                        v-if="curCountry && curCountry.name === 'Montenegro'"
                        class="py-0 d-flex align-center h40 mt-2"
                        cols="12"
                        sm="6"
                    >
                        <v-checkbox
                            v-if="form.taxes_enabled"
                            v-model="form.reverse_calculation"
                            class="mt-2"
                            :label="$t('get_started.reverse_calculation')"
                            hide-details
                            validate-on-blur
                        />
                    </v-col>
                </v-row>
            </div>
        </v-slide-y-transition>

        <div class="footer">
            <v-btn
                class="flex-1"
                color="primary"
                depressed
                :disabled="form.busy"
                :loading="form.busy"
                @click="save"
            >
                {{ $t('general.next') }}
            </v-btn>
        </div>
    </v-form>
</template>

<script>
import ApiService from '@/services/ApiService'
import TenantService from '@/services/TenantService'
import Countries from '@/utilities/Countries';
import i18n from '@/i18n'

export default {
    created() {
        this.countries = Countries.data;
        this.curCountry = this.countries.find(c => {
            if(locale === 'me') return c.alpha3Code === 'MNE';
            if(locale === 'ba') return c.alpha3Code === 'BIH';
            return c.alpha3Code === 'SRB';
        })
    },

    data() {
        return {
            form: new CodForm({
                name: null,
                admin_pin: null,
                admin_pin_confirmation: null,
                address: null,
                country: null,
                locale: null,
                currency: null,
                tax_id_number: null,
                registration_number: null,
                bank_account_number: null,
                hash: null,
                company_name: null,
                company_address: null,
                postal_number: null,
                city: null,
                operator_code: null,
                cert: null,
                cert_password: null,
                taxes_enabled: true,
                noncash_only: false,
                reverse_calculation: false,

                rules: {
                    name: [CodForm.rule('required')],
                    admin_pin: [CodForm.rule('required'), CodForm.rule('pin')],
                    admin_pin_confirmation: [
                        CodForm.rule('required'),
                        CodForm.rule('pin'),
                        CodForm.rule('match', () => this.form.admin_pin)
                    ],
                    city: [CodForm.rule('required')],
                    postal_number: [CodForm.rule('required')],
                    address: [CodForm.rule('required')],
                    country: [CodForm.rule('required')],
                    tax_id_number: [CodForm.rule('required_if', () => {
                        return this.curCountry && ['Montenegro', 'Serbia', 'Bosnia and Herzegovina'].includes(this.curCountry.name)
                    })],
                    registration_number: [CodForm.rule('required_if', () => this.curCountry?.name === 'Serbia')],
                    bank_account_number: [CodForm.rule('required_if', () => this.curCountry?.name === 'Serbia')],
                    hash: [
                        CodForm.rule('required_if', () => this.curCountry?.name === 'Montenegro'),
                        CodForm.rule('min_length', 10)
                    ],
                    company_name: [CodForm.rule('required_if', () => {
                        return this.curCountry && ['Montenegro', 'Serbia', 'Bosnia and Herzegovina'].includes(this.curCountry.name)
                    })],
                    company_address: [CodForm.rule('required_if', () => {
                        return this.curCountry && ['Montenegro', 'Serbia', 'Bosnia and Herzegovina'].includes(this.curCountry.name)
                    })],
                    operator_code: [CodForm.rule('required_if', () => {
                        return this.curCountry && this.curCountry.name === 'Montenegro'
                    }), CodForm.rule('min_length', 10)],
                    cert: [CodForm.rule('required_if', () => {
                        return this.curCountry && this.curCountry.name === 'Montenegro'
                    })],
                    cert_password: [CodForm.rule('required_if', () => {
                        return this.curCountry && this.curCountry.name === 'Montenegro'
                    })],
                }
            }),

            countries: [],
            curCountry: null,

            showPin: false,
            showCertPassword: false,

            loadingTaxIdNumber: false,
        };
    },

    watch: {
        curCountry(val) {
            if(val && ['Montenegro', 'Croatia'].includes(val.name)) {
                i18n.locale = 'me';
            } else if (val.name === 'Bosnia and Herzegovina') {
                i18n.locale = 'ba';
            } else if (val.name === 'Serbia') {
                i18n.locale = 'sr';
            } else {
                i18n.locale = 'en';
            }
        },

        'form.taxes_enabled'(val) {
            if(!val) {
                this.form.reverse_calculation = false
            }
        },

        'form.tax_id_number'(val) {
            if (this.curCountry && this.curCountry?.name === 'Serbia' && val && val.length === 9) {
                this.searchTaxIdNumber(val, 'SRB')
            }

            if (this.curCountry && this.curCountry?.name === 'Montenegro' && val && val.length === 8) {
                this.searchTaxIdNumber(val, 'ME')
            }
        },
    },
    
    computed: {
        isDisabled() {
            return this.form.busy || this.loadingTaxIdNumber;
        },
    },

    methods: {

        /**
         * Save Venue
         */
        save() {
            if (!this.$refs.form.validate()) return;

            this.form.country = this.curCountry.name;
            this.form.currency = this.curCountry.currencies[0].code;
            if (this.curCountry.name === 'Montenegro') {
                this.form.locale = 'me';
            } else if (this.curCountry.name === 'Bosnia and Herzegovina') {
                this.form.locale = 'ba';
            } else if (this.curCountry.name === 'Serbia') {
                this.form.locale = 'sr'
            } else {
                this.form.locale = this.curCountry.alpha2Code.toLowerCase()
            }

            ApiService.post('/getstarted/venue', this.form).then(response => {
                TenantService.setTenant(response.data.tenant);
                TenantService.setPkey(response.data.pkey)
                this.$emit('finish')
            })
        },

        searchTaxIdNumber(val, countryCode){
            this.loadingTaxIdNumber = true;

            ApiService.get(`/clients/search/${val}`, {
                params: {
                    countryCode: countryCode
                }
            }).then((response) => {
                if (response.data.success) {
                    this.form.company_name = response.data.company.short_name;
                    this.form.company_address = response.data.company.address;
                    this.form.city = response.data.company.city;
                    this.form.registration_number = response.data.company.registration_number;
                    this.form.bank_account_number = response.data.company.bank_account_number;
                }
            })
            .finally(() => {
                this.loadingTaxIdNumber = false;
            });
        }
    },
};
</script>

<style lang="scss" scoped>
.venue-setup {
    .step-title {
        margin-bottom: 10px;
        font-size: 20px;
    }

    .step-description {
        margin-bottom: 30px;
        font-size: 14px;
        color: #8f9095;
    }

    .footer {
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: flex-start;
    }

    .section {
        padding: 12px 16px;
        background: #292b38;
        border-radius: 4px;
        margin-bottom: 56px;
        margin-top: 8px;
    }
}
</style>
